/**
 * @module search-page-header
 *
 * Drives the `<search-page-header>` custom element on the search page.
 *
 * Responsibilities:
 * - Handles the inline search input with edit / clear controls.
 * - Submits searches without a full page reload by fetching fresh section
 *   HTML via the Section Rendering API.
 * - Populates "similar searches" and "related categories" suggestions from
 *   the predictive-search endpoint.
 *
 * The header section is never morphed as a whole — only data attributes,
 * the description copy, and suggestion containers are updated via targeted
 * DOM writes so the active input is never disturbed.
 *
 * @see sections/search-header.liquid — the Liquid section that renders this component.
 */

import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';

/** Maximum characters accepted by the search input. */
const MAX_SEARCH_LENGTH = 150;

/** Delay (ms) before checking if focus has left the component on blur. */
const BLUR_DELAY = 150;

/** Debounce interval (ms) for the live search-as-you-type handler. */
const DEBOUNCE_DELAY = 300;

/**
 * Extracts the Shopify section ID from the closest `.shopify-section`
 * wrapper, stripping the `shopify-section-` prefix that Shopify adds.
 *
 * @param {Element | null} element - Any element nested inside a section wrapper.
 * @returns {string | undefined} The bare section ID, or undefined if none found.
 */
const getSectionId = (element) =>
  element?.closest('.shopify-section')?.id?.replace(/^shopify-section-/, '');

/**
 * @typedef {object} Refs
 * @property {HTMLFormElement}  form                  - The search `<form>`.
 * @property {HTMLInputElement} searchInput            - The `<input type="search">`.
 * @property {HTMLElement}      [suggestionsContainer] - Outer wrapper for suggestions.
 * @property {HTMLElement}      [suggestionsRow]       - Row layout inside the suggestions area.
 * @property {HTMLElement}      [similarSearches]      - Container for "similar searches" links.
 * @property {HTMLElement}      [relatedCategories]    - Container for "related categories" links.
 */

/**
 * Initialises all behaviour for a `<search-page-header>` element.
 *
 * All internal state is held in closures so nothing leaks onto the host
 * beyond the event-handler properties that the Component event-delegation
 * system expects.
 *
 * @param {SearchPageHeader} host - The custom-element instance to bind to.
 */
function initSearchPageHeader(host) {
  /**
   * Controller for the in-flight predictive-search fetch.
   * Aborted whenever a newer request supersedes it.
   *
   * @type {AbortController | null}
   */
  let activeFetch = null;

  /**
   * Monotonically increasing counter used to discard stale responses from
   * `renderSearchResults`. Each call bumps the counter; when the response
   * arrives it is compared against the current value and dropped if a
   * newer request has been issued in the meantime.
   *
   * @type {number}
   */
  let searchGeneration = 0;

  /**
   * Parses HTML returned by the predictive-search endpoint and injects
   * matching suggestion / category fragments into the header's ref containers.
   *
   * @param {string} html - Full HTML string from the predictive-search section render.
   */
  function renderSuggestions(html) {
    const { similarSearches, relatedCategories, suggestionsContainer } = host.refs;
    if (!suggestionsContainer) return;

    const doc = new DOMParser().parseFromString(html, 'text/html');
    const suggestionsEl = doc.querySelector('.search-suggestions');
    const categoriesEl = doc.querySelector('.search-categories');

    if (!suggestionsEl && !categoriesEl) return;

    if (similarSearches && suggestionsEl) {
      similarSearches.innerHTML = suggestionsEl.outerHTML;
    }

    if (relatedCategories && categoriesEl) {
      relatedCategories.innerHTML = categoriesEl.outerHTML;
    }
  }

  /**
   * Applies targeted DOM updates from freshly fetched header HTML without
   * replacing the entire element (which would destroy input focus and state).
   *
   * Updates:
   * - `data-has-results` / `data-no-results` attributes (drive CSS visibility).
   * - `data-search-terms` attribute.
   * - Description paragraph (result count / "no results" copy).
   * - Similar-searches and related-categories ref containers.
   *
   * @param {string} html - Full section HTML containing a `<search-page-header>`.
   */
  function updateHeaderState(html) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    /** @type {HTMLElement | null} */
    const newHeader = doc.querySelector('search-page-header');
    if (!newHeader) return;

    host.toggleAttribute('data-has-results', newHeader.hasAttribute('data-has-results'));
    host.toggleAttribute('data-no-results', newHeader.hasAttribute('data-no-results'));

    const searchTerms = newHeader.getAttribute('data-search-terms');
    if (searchTerms !== null) {
      host.dataset.searchTerms = searchTerms;
    }

    const newDescription = doc.querySelector('.search-page-header__description');
    const currentDescription = host.querySelector('.search-page-header__description');

    if (newDescription && currentDescription) {
      currentDescription.innerHTML = newDescription.innerHTML;
    }

    const newSimilar = doc.querySelector('[ref="similarSearches"]');
    const newRelated = doc.querySelector('[ref="relatedCategories"]');
    const { similarSearches, relatedCategories } = host.refs;

    if (similarSearches && newSimilar) {
      similarSearches.innerHTML = newSimilar.innerHTML;
    }

    if (relatedCategories && newRelated) {
      relatedCategories.innerHTML = newRelated.innerHTML;
    }
  }

  /**
   * Performs a search by fetching fresh section HTML for both the header
   * and the results list, then applies updates without a full navigation.
   *
   * The browser URL is updated via `history.replaceState` so the address
   * bar stays in sync. A generation counter guards against stale responses
   * when multiple requests overlap.
   *
   * @param {string} query - The raw search query (will be truncated to {@link MAX_SEARCH_LENGTH}).
   */
  async function renderSearchResults(query) {
    const generation = ++searchGeneration;
    const searchUrl = host.dataset.searchUrl || '/search';
    const url = new URL(searchUrl, location.origin);
    url.searchParams.set('type', 'product');
    url.searchParams.set('q', query.slice(0, MAX_SEARCH_LENGTH));

    history.replaceState(null, '', url);

    const headerSectionId = getSectionId(host);
    const resultsSectionId = getSectionId(document.querySelector('results-list'));

    const fetches = [];

    fetches.push(
      headerSectionId
        ? sectionRenderer.getSectionHTML(headerSectionId, false, new URL(url))
        : Promise.resolve(null),
    );

    fetches.push(
      resultsSectionId
        ? sectionRenderer.renderSection(resultsSectionId, { url: new URL(url), cache: false })
        : Promise.resolve(null),
    );

    const [headerHTML] = await Promise.all(fetches);

    if (generation !== searchGeneration) return;

    if (headerHTML) {
      updateHeaderState(headerHTML);
    }
  }

  /**
   * Fetches predictive-search results (suggested queries and collections)
   * from Shopify's predictive-search endpoint and renders them into the
   * suggestion containers.
   *
   * Automatically aborts any in-flight request before starting a new one.
   * Silently swallows errors (aborted or failed fetches).
   *
   * @param {string} term - The search term to look up suggestions for.
   */
  async function fetchSuggestions(term) {
    if (!term) return;

    activeFetch?.abort();
    const controller = new AbortController();
    activeFetch = controller;

    const url = new URL(Theme.routes.predictive_search_url, location.origin);
    url.searchParams.set('q', term);
    url.searchParams.set('resources[type]', 'query,collection');
    url.searchParams.set('resources[limit]', '4');

    try {
      const html = await sectionRenderer.getSectionHTML('predictive-search', false, url);
      if (controller.signal.aborted || !html) return;

      renderSuggestions(html);
    } catch {
      // Request was aborted or failed — nothing to handle.
    }
  }

  // ── Event handlers (bound to the host for Component event delegation) ──

  /** Focuses the search input and enters the active editing state. */
  host.onEditClick = () => {
    const input = host.refs.searchInput;
    input.focus();
    input.setSelectionRange(input.value.length, input.value.length);
    host.setAttribute('data-active', '');
  };

  /** Clears the search input, re-focuses it, and resets the results. */
  host.onClearClick = () => {
    host.refs.searchInput.value = '';
    host.refs.searchInput.focus();

    if (host.dataset.searchUrl) {
      renderSearchResults('');
    }
  };

  /** Enters the active editing state when the input receives focus. */
  host.onInputFocus = () => {
    host.setAttribute('data-active', '');
  };

  /**
   * Exits the active editing state after a short delay, but only if
   * focus has moved outside the component entirely (prevents flickering
   * when focus shifts between child elements).
   */
  host.onInputBlur = () => {
    setTimeout(() => {
      if (!host.contains(document.activeElement)) {
        host.removeAttribute('data-active');
      }
    }, BLUR_DELAY);
  };

  /**
   * Debounced handler for live search-as-you-type. Fires after the buyer
   * stops typing for {@link DEBOUNCE_DELAY}ms. Empty input clears results
   * immediately.
   */
  host.onSearchInput = debounce(async () => {
    const value = host.refs.searchInput.value.trim().slice(0, MAX_SEARCH_LENGTH);

    if (value.length === 0) {
      activeFetch?.abort();
      renderSearchResults('');
      return;
    }

    await renderSearchResults(value);
    fetchSuggestions(value);
  }, DEBOUNCE_DELAY);

  /**
   * Handles explicit form submission (Enter key or submit button).
   * Cancels any pending debounced input handler, then immediately
   * fetches results and suggestions for the current query.
   *
   * @param {SubmitEvent} event
   */
  host.onFormSubmit = async (event) => {
    event.preventDefault();

    const value = host.refs.searchInput.value.trim().slice(0, MAX_SEARCH_LENGTH);
    if (value.length === 0) return;

    host.onSearchInput.cancel();
    await renderSearchResults(value);
    fetchSuggestions(value);
  };

  host.addEventListener('click', (event) => {
    const target = /** @type {Element} */ (event.target);
    const link = target.closest('.search-suggestions__link');
    if (!link) return;

    const url = new URL(/** @type {HTMLAnchorElement} */ (link).href, location.origin);
    const query = url.searchParams.get('q');
    if (query == null) return;

    event.preventDefault();
    host.onSearchInput.cancel();
    host.refs.searchInput.value = query;
    host.refs.searchInput.blur();
    host.removeAttribute('data-active');

    renderSearchResults(query);
    fetchSuggestions(query);
  });

  if (host.dataset.searchTerms) {
    fetchSuggestions(host.dataset.searchTerms);
  }
}

/**
 * Thin custom-element shell for `<search-page-header>`.
 *
 * All behaviour lives in {@link initSearchPageHeader}; this class only
 * exists because `customElements.define` requires a constructor and the
 * Component event-delegation system requires an `instanceof Component`.
 *
 * @extends {Component<Refs>}
 */
class SearchPageHeader extends Component {
  requiredRefs = ['form', 'searchInput'];

  /** @type {() => void} */
  onEditClick;

  /** @type {() => void} */
  onClearClick;

  /** @type {() => void} */
  onInputFocus;

  /** @type {() => void} */
  onInputBlur;

  /** @type {ReturnType<typeof debounce>} */
  onSearchInput;

  /** @type {(event: SubmitEvent) => Promise<void>} */
  onFormSubmit;

  connectedCallback() {
    super.connectedCallback();
    initSearchPageHeader(this);
  }
}

if (!customElements.get('search-page-header')) {
  customElements.define('search-page-header', SearchPageHeader);
}
